import {
  Bloodwork,
  BloodworkAnalysis,
  PanelMarker,
  PanelRange,
  Client,
  Analysis,
} from '../models';
import { MathUtils } from '../utils';

export class AIExportSerializer {
  static replacer(key: string, value: any): any {
    // Handle BloodworkAnalysis
    if (value instanceof BloodworkAnalysis) {
      const {
        bloodworkRanges,
        bloodworkFields,
        panelMarkers, // This is an array generated in init(), but we want the properties directly
        immuneSystemDisorderProbability,
        immuneSystemDisorderProbabilityCategory,
        prostateAnalysis,
        COMMON_IRON_TIBC_SATURATION_FERRITIN,
        possibleRootCauseCategories,

        // Remove nested analysis objects that contain root causes
        boneMarrowAnalysis,
        electrolytesAnalysis,
        glucoseAnalysis,
        hormonesAnalysis,
        inflammationOxidativeStressAnalysis,
        ironAnalysis,
        kidneyAnalysis,
        lipidsAnalysis,
        liverBiliaryAnalysis,
        nutrientsAnalysis,
        plateletsAnalysis,
        proteinsAnalysis,
        thyroidAnalysis,
        wbcAnalysis,

        ...rest
      } = value as any;

      // Filter out null/undefined properties that are likely PanelMarkers
      const filtered: any = {};
      Object.keys(rest).forEach(key => {
        const val = rest[key];
        // Keep valid calculations/markers
        if (val && typeof val === 'object' && val instanceof PanelMarker) {
          if (val.hasValue && val.hasValue()) {
            filtered[key] = val;
          }
        } else if (val !== null && val !== undefined) {
          // Keep other non-marker properties (like bloodwork object)
          filtered[key] = val;
        }
      });
      return filtered;
    }

    // Handle Bloodwork
    if (value instanceof Bloodwork) {
      const {
        client,
        collectionDate,
        bloodworkNotes,
        menstrualPhase,
      } = value as any;

      const result: any = {
        client,
        collectionDate,
      };

      // Menstrual phase drives which FSH/LH/estradiol reference ranges apply
      // (e.g. follicular vs luteal), so surface it for AI interpretation of the
      // sex-hormone markers. Only include when meaningfully set (not "None").
      if (menstrualPhase && menstrualPhase !== 'None') {
        result.menstrualPhase = menstrualPhase;
      }

      // Filter notes
      if (
        bloodworkNotes &&
        bloodworkNotes.notes &&
        Array.isArray(bloodworkNotes.notes)
      ) {
        const filteredNotes = bloodworkNotes.notes.filter((note: any) => {
          return note.text && note.text.trim().length > 0;
        });

        // Only include if there are valid notes
        if (filteredNotes.length > 0) {
          result.bloodworkNotes = { notes: filteredNotes };
        }
      }

      return result;
    }

    // Handle Client
    if (value instanceof Client) {
      const {
        id,
        practitionerId,
        createDate,
        lastModifiedDate,
        firstName,
        lastName,
        alcoholDrinker,
        cigaretteSmoker,
        cigarettesSmoked,
        standardDrinks,
        // Client-level "Overall Practitioner Notes" is a per-test SNAPSHOT here and
        // can be stale; the export sources it from the LIVE client at the top level
        // instead, so strip it from the embedded client snapshot.
        overallPractitionerNotes,
        ...rest
      } = value as any;

      const formatted: any = { ...rest };

      // Format habits
      formatted.alcoholConsumption = `${standardDrinks ?? 0}/week`;
      formatted.cigarettesSmoking = `${cigarettesSmoked ?? 0}/day`;

      return formatted;
    }

    // Handle PanelMarker
    if (value instanceof PanelMarker) {
      const { field, previousPanelMarker, attribute, range, ...rest } = value;
      const json: any = { ...rest };

      // Add units from field if available
      if (field?.units) {
        json.units = field.units;
      }

      // We want to include range but stripped (PanelRange handling below)
      if (range) {
        json.range = range;
      }

      if (typeof json.percentFromMean === 'number') {
        json.percentFromMean = MathUtils.round(json.percentFromMean, 2);
      }
      return json;
    }

    // Handle PanelRange
    if (value instanceof PanelRange) {
      const {
        id,
        gender,
        ethnicity,
        ageMin,
        ageMax,
        precision,
        mpvMin,
        mpvMax,
        elevationMin,
        elevationMax,
        ...rest
      } = value;
      return rest;
    }

    // Handle Analysis (base class for all specific analyses like WBCAnalysis, etc)
    // We check for 'ba' property which is the circular reference.
    if (value instanceof Analysis) {
      const { ba, possibleRootCauseCategories, ...rest } = value as any;
      return rest;
    }

    return value;
  }
}
